import {
  INITIAL_SURVEY_TO_SHOW,
  EXPIRY_PERIOD,
  SURVEY_COUNT_LIMIT,
  SURVEY_TIMEOUT_TO_SHOW,
  SURVEY_DATA_LOCAL_STORAGE_KEY,
  SURVEY_SESSION_ACTIVE_KEY,
  SURVEY_SHOWN_THIS_SESSION_KEY
} from 'enums/surveyRotation'
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      surveyCounter: 0,
      isNewSession: false,
      expiryTimerId: null,
      isInitialized: false,
      surveyScheduled: false
    }
  },
  computed: {
    ...mapGetters({
      isBrowserTabActiveAndFocused: 'isBrowserTabActiveAndFocused'
    }),
    isNextSurveyAvailable() {
      return this.surveyCounter <= SURVEY_COUNT_LIMIT
    }
  },
  watch: {
    isBrowserTabActiveAndFocused: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.isInitialized) {
          this.$_surveyRotation_handleTabActive()
        } else {
          this.$_surveyRotation_handleTabInactive()
        }
      }
    }
  },
  methods: {
    $_surveyRotation_initialization() {
      const surveyData = localStorage.getItem(SURVEY_DATA_LOCAL_STORAGE_KEY)

      if (!surveyData) {
        this.$_surveyRotation_createInitialSurveyData()
        return
      }

      try {
        const parsed = JSON.parse(surveyData)
        this.surveyCounter = parsed.counter
      } catch (err) {
        this.$errorHandler(err, this)
      }
    },
    $_surveyRotation_createInitialSurveyData() {
      const surveyData = {
        counter: 1,
        startDate: Date.now(),
        expiryDate: Date.now() + EXPIRY_PERIOD
      }

      localStorage.setItem(
        SURVEY_DATA_LOCAL_STORAGE_KEY,
        JSON.stringify(surveyData)
      )

      this.surveyCounter = INITIAL_SURVEY_TO_SHOW
    },
    $_surveyRotation_detectNewSession() {
      const sessionFlag = sessionStorage.getItem(SURVEY_SESSION_ACTIVE_KEY)

      if (!sessionFlag) {
        this.isNewSession = true
        sessionStorage.setItem(SURVEY_SESSION_ACTIVE_KEY, 'true')
      } else {
        this.isNewSession = false
      }
    },
    $_surveyRotation_handleExpiredSession() {
      let surveyData

      try {
        surveyData = JSON.parse(
          localStorage.getItem(SURVEY_DATA_LOCAL_STORAGE_KEY)
        )
      } catch (err) {
        this.$errorHandler(err, this)
      }

      if (!surveyData) return

      const currentDate = Date.now()
      const expiryDate = surveyData.expiryDate

      const isSessionExpired = currentDate > expiryDate

      if (!isSessionExpired) {
        this.$_surveyRotation_scheduleExpiryTimer(currentDate, expiryDate)

        return
      }

      this.$_surveyRotation_resetExpiredSessionAndShowNextSurvey(
        surveyData,
        currentDate
      )
    },
    $_surveyRotation_shouldScheduleSurvey() {
      if (!this.isNewSession) {
        return false
      }

      if (!this.isNextSurveyAvailable) {
        return false
      }

      const shownThisSession = sessionStorage.getItem(
        SURVEY_SHOWN_THIS_SESSION_KEY
      )

      if (shownThisSession) {
        return false
      }

      return true
    },
    $_surveyRotation_handleScheduleSurveyDisplay() {
      if (this.surveyScheduled) {
        return
      }

      this.surveyScheduled = true

      setTimeout(() => {
        if (!this.isBrowserTabActiveAndFocused) {
          this.surveyScheduled = false
          return
        }

        if (this.$_surveyRotation_isScheduledSurveyAlreadyShown()) {
          this.surveyScheduled = false
          return
        }

        this.$bus.$emit('show-popup-survey', {
          surveyCounter: this.surveyCounter
        })

        this.$_surveyRotation_markSurveyShownThisSession()
        this.surveyScheduled = false
      }, SURVEY_TIMEOUT_TO_SHOW)
    },
    $_surveyRotation_isScheduledSurveyAlreadyShown() {
      let surveyData

      try {
        surveyData = JSON.parse(
          localStorage.getItem(SURVEY_DATA_LOCAL_STORAGE_KEY)
        )
      } catch (err) {
        this.$errorHandler(err, this)
      }

      return surveyData.counter > this.surveyCounter
    },
    $_surveyRotation_markSurveyShownThisSession() {
      sessionStorage.setItem(SURVEY_SHOWN_THIS_SESSION_KEY, 'true')
      this.$_surveyRotation_incrementCounter()
    },
    $_surveyRotation_incrementCounter() {
      const surveyData = JSON.parse(
        localStorage.getItem(SURVEY_DATA_LOCAL_STORAGE_KEY)
      )

      if (!surveyData) return

      if (this.isNextSurveyAvailable) {
        surveyData.counter += 1
        localStorage.setItem(
          SURVEY_DATA_LOCAL_STORAGE_KEY,
          JSON.stringify(surveyData)
        )
        this.surveyCounter = surveyData.counter
      }
    },
    $_surveyRotation_scheduleExpiryTimer(currentDate, expiryDate) {
      const expiringTimeRemaining = expiryDate - currentDate

      this.expiryTimerId = setTimeout(() => {
        this.$_surveyRotation_handleExpiredSession()
      }, expiringTimeRemaining)
    },
    $_surveyRotation_resetExpiredSessionAndShowNextSurvey(
      surveyData,
      currentDate
    ) {
      if (this.isNextSurveyAvailable) {
        surveyData.startDate = currentDate
        surveyData.expiryDate = currentDate + EXPIRY_PERIOD

        localStorage.setItem(
          SURVEY_DATA_LOCAL_STORAGE_KEY,
          JSON.stringify(surveyData)
        )

        this.surveyCounter = surveyData.counter

        this.$_surveyRotation_handleScheduleSurveyDisplay()
      }
    },
    $_surveyRotation_handleTabActive() {
      if (!process.client) return

      this.$_surveyRotation_handleExpiredSession()
    },
    $_surveyRotation_handleTabInactive() {
      if (this.expiryTimerId) {
        clearTimeout(this.expiryTimerId)
        this.expiryTimerId = null
      }
    }
  },
  mounted() {
    this.$_surveyRotation_initialization()
    this.$_surveyRotation_detectNewSession()
    this.isInitialized = true

    if (this.isBrowserTabActiveAndFocused) {
      this.$_surveyRotation_handleTabActive()
    }

    if (this.$_surveyRotation_shouldScheduleSurvey()) {
      this.$_surveyRotation_handleScheduleSurveyDisplay()
    }
  }
}
